import React from 'react'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import { formatDuration, intervalToDuration } from 'date-fns'

import type { Service } from 'common/api/v1/types'
import { GridItem, Paper } from '../common/Form'
import DataSet from '../common/DataSet'
import { ExternalLink } from '../common/Link'
import { ProductLink } from './Product'

export interface MetaProps {
  service: Service
}

const VolumeCapacityList = ({ currentVolumes }: Pick<Service, 'currentVolumes'>) => {
  if (!currentVolumes) return 'N/A'

  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
      {currentVolumes.map((volume) => (
        <Chip key={volume.name} label={`${volume.name}: ${volume.currentVolumeCapacity}`} size="small" />
      ))}
    </Box>
  )
}

const RetentionPeriodList = ({ retentionPeriods }: Required<Pick<Service, 'retentionPeriods'>>) => {
  const pretty = (ms: number) => {
    if (ms < 0) return 'N/A'
    const duration = intervalToDuration({ start: 0, end: ms })
    return formatDuration(duration)
  }
  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
      {retentionPeriods.map((period) => (
        <Chip key={period.name} label={`${period.name}: ${pretty(period.current)}`} size="small" />
      ))}
    </Box>
  )
}

export const Meta: React.FunctionComponent<MetaProps> = ({ service }) => {
  const logs = {
    Logs: (
      <ExternalLink underline="always" href={service.filterUrl}>
        Logs
      </ExternalLink>
    ),
  }

  const meta = {
    Purpose: service.purpose,
    Product: (
      <>
        {service.product}
        <ProductLink product={service.product} enabled={service.enabled} />
      </>
    ),
    ...logs,
    'Current volume capacity': <VolumeCapacityList currentVolumes={service.currentVolumes} />,
    ...(service.retentionPeriods
      ? { 'Retention period': <RetentionPeriodList retentionPeriods={service.retentionPeriods} /> }
      : {}),
  }

  return (
    <Paper title="Metadata" collapsible actionsPane={[]}>
      <GridItem lg={12} xl={12}>
        <DataSet values={meta} />
      </GridItem>
    </Paper>
  )
}
